import { useMemo, useState } from "react";
import { Checkbox, Color, Flex, Grid, Scroller, Search, Text } from "src/elements";
import { StoresSelectorStore } from "src/components/StoresSelector/store/types";
import { CheckboxProps } from "src/elements/Checkbox/Checkbox";
import { useTranslation } from "src/i18n";

type StoreColumnKey = "store" | "bay_size" | "business_type" | "activity_type";

const COLUMNS: { key: StoreColumnKey; labelKey: string }[] = [
  { key: "store", labelKey: "storesPickList.columnStore" },
  { key: "bay_size", labelKey: "storesPickList.columnBaySize" },
  { key: "business_type", labelKey: "storesPickList.columnBusinessType" },
  { key: "activity_type", labelKey: "storesPickList.columnActivityType" },
];

const GRID_COLUMNS = "24px repeat(4, 1fr)";
const ROW_PADDING = "6px 8px";

interface Props {
  stores: StoresSelectorStore[];
  selectedCodes: Set<string>;
  setSelectedCodes: (updater: (prev: Set<string>) => Set<string>) => void;
}

export const StoresPickList = ({ stores, selectedCodes, setSelectedCodes }: Props) => {
  const { t, tDynamic } = useTranslation("common");
  const [search, setSearch] = useState("");

  const visibleStores = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return stores;
    return stores.filter((s) => s.store.toLowerCase().includes(query));
  }, [stores, search]);

  const visibleCodes = useMemo(() => visibleStores.map((s) => s.store), [visibleStores]);
  const selectedVisibleCount = visibleCodes.filter((code) => selectedCodes.has(code)).length;

  const allVisibleChecked: CheckboxProps["isChecked"] =
    visibleCodes.length === 0 || selectedVisibleCount === 0
      ? false
      : selectedVisibleCount === visibleCodes.length
        ? true
        : "mixed";

  const toggleOne = (code: string, isChecked: boolean) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (isChecked) {
        next.add(code);
      } else {
        next.delete(code);
      }
      return next;
    });
  };

  const toggleAllVisible = (isChecked: boolean) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      for (const code of visibleCodes) {
        if (isChecked) {
          next.add(code);
        } else {
          next.delete(code);
        }
      }
      return next;
    });
  };

  return (
    <Flex column gap="10px" flexGrow>
      <Flex justify="between" align="center" gap="10px">
        <Search
          search={search}
          setSearch={setSearch}
          placeholder={t("storesPickList.searchPlaceholder")}
          width="240px"
        />
        <Text variant="caption1">
          {t("storesPickList.selectedCount", {
            selected: String(selectedCodes.size),
            total: String(stores.length),
          })}
        </Text>
      </Flex>

      <Grid columns={GRID_COLUMNS} gap="10px" align="center" padding={ROW_PADDING}>
        <Checkbox
          isChecked={allVisibleChecked}
          setIsChecked={toggleAllVisible}
          isCheckboxLeft
          isDisabled={visibleCodes.length === 0}
        />
        {COLUMNS.map((col) => (
          <Text key={col.key} variant="body1">
            {tDynamic(col.labelKey)}
          </Text>
        ))}
      </Grid>

      <Scroller size="thin" maxHeight="320px">
        {visibleStores.map((store) => {
          const isSelected = selectedCodes.has(store.store);

          return (
            <Grid
              key={store.store}
              columns={GRID_COLUMNS}
              gap="10px"
              align="center"
              padding={ROW_PADDING}
              borderRadius="6px"
              background={isSelected ? Color.greenSmoke : undefined}
              backgroundHovered={isSelected ? Color.greenSmokeHover : Color.lightGray}
            >
              <Checkbox
                isChecked={isSelected}
                setIsChecked={(isChecked) => toggleOne(store.store, isChecked)}
                isCheckboxLeft
              />
              {COLUMNS.map((col) => (
                <Text key={col.key} variant="body2">
                  {store[col.key] ?? "-"}
                </Text>
              ))}
            </Grid>
          );
        })}
      </Scroller>
    </Flex>
  );
};
